import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import '../styles/TicketStatus.css';

interface JobStatus {
  id: string;
  state: 'waiting' | 'active' | 'completed' | 'failed';
  progress: number;
  failedReason?: string;
}

function TicketStatus() {
  const navigate = useNavigate();
  const [jobId, setJobId] = useState<string | null>(null);
  const [bookingId, setBookingId] = useState<string | null>(null);
  const [jobStatus, setJobStatus] = useState<JobStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // جلب معرف المهمة من localStorage
    const storedJobId = localStorage.getItem('ticketJobId');
    const storedBookingId = localStorage.getItem('bookingId');
    
    if (!storedJobId || !storedBookingId) {
      setError('لا توجد مهمة قيد المعالجة');
      setLoading(false);
      return;
    }
    
    setJobId(storedJobId);
    setBookingId(storedBookingId);
  }, []);

  useEffect(() => {
    if (!jobId) return;

    const checkStatus = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/api/ticket-generation/job-status/${jobId}`);
        
        if (!response.ok) {
          throw new Error('فشل جلب حالة المهمة');
        }
        
        const data = await response.json();
        
        if (data.success && data.job) {
          setJobStatus(data.job);
          
          // إذا اكتملت المهمة، امسح البيانات من localStorage
          if (data.job.state === 'completed') {
            localStorage.removeItem('ticketJobId');
            localStorage.removeItem('bookingId');
          }
        }
      } catch (err) {
        console.error('Error checking job status:', err);
        setError('حدث خطأ في جلب حالة المهمة');
      } finally {
        setLoading(false);
      }
    };

    // تحقق من الحالة كل 2 ثانية
    checkStatus();
    const interval = setInterval(checkStatus, 2000);

    return () => clearInterval(interval);
  }, [jobId]);

  const getStatusText = () => {
    if (!jobStatus) return 'جاري التحميل...';
    
    switch (jobStatus.state) {
      case 'waiting':
        return 'في انتظار المعالجة...';
      case 'active':
        return `جاري معالجة التذاكر... ${jobStatus.progress}%`;
      case 'completed':
        return 'تم إنشاء التذاكر بنجاح! 🎉';
      case 'failed':
        return `فشلت المعالجة: ${jobStatus.failedReason || 'خطأ غير معروف'}`;
      default:
        return 'حالة غير معروفة';
    }
  };

  const getProgressColor = () => {
    if (!jobStatus) return '#ccc';
    
    switch (jobStatus.state) {
      case 'waiting':
        return '#ffa500';
      case 'active':
        return '#3498db';
      case 'completed':
        return '#27ae60';
      case 'failed':
        return '#e74c3c';
      default:
        return '#ccc';
    }
  };

  if (loading && !jobStatus) {
    return (
      <div className="ticket-status-page">
        <div className="loading-container">
          <div className="spinner"></div>
          <p>جاري التحميل...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="ticket-status-page">
        <div className="error-container">
          <h2>خطأ</h2>
          <p>{error}</p>
          <button onClick={() => navigate('/')}>العودة للرئيسية</button>
        </div>
      </div>
    );
  }

  return (
    <motion.div 
      className="ticket-status-page"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
    >
      <div className="status-container">
        <h1>حالة معالجة التذاكر</h1>
        
        <div className="status-info">
          <div className="status-icon">
            {jobStatus?.state === 'completed' && '✅'}
            {jobStatus?.state === 'failed' && '❌'}
            {(jobStatus?.state === 'waiting' || jobStatus?.state === 'active') && '⏳'}
          </div>
          
          <h2>{getStatusText()}</h2>
          
          {jobStatus && jobStatus.state === 'active' && (
            <div className="progress-container">
              <div className="progress-bar">
                <div 
                  className="progress-fill"
                  style={{ 
                    width: `${jobStatus.progress}%`,
                    backgroundColor: getProgressColor()
                  }}
                />
              </div>
              <span className="progress-text">{jobStatus.progress}%</span>
            </div>
          )}
          
          {jobStatus?.state === 'completed' && (
            <div className="success-message">
              <p>تم إنشاء جميع التذاكر وإرسالها إلى بريدك الإلكتروني بنجاح!</p>
              <p>يمكنك الآن تحميل التذاكر من البريد الإلكتروني.</p>
            </div>
          )}
          
          {jobStatus?.state === 'failed' && (
            <div className="error-details">
              <p>حدث خطأ أثناء معالجة التذاكر.</p>
              <p className="error-reason">{jobStatus.failedReason}</p>
              <button 
                onClick={() => navigate('/contact')}
                className="contact-button"
              >
                تواصل معنا للمساعدة
              </button>
            </div>
          )}
        </div>
        
        <div className="action-buttons">
          <button 
            onClick={() => navigate('/')}
            className="home-button"
          >
            العودة للرئيسية
          </button>
          
          {bookingId && (
            <button 
              onClick={() => navigate(`/booking/${bookingId}`)}
              className="booking-button"
            >
              عرض تفاصيل الحجز
            </button>
          )}
        </div>
      </div>
    </motion.div>
  );
}

export default TicketStatus;